import React, { useState } from 'react';

import { getRem, isMobileScreen } from 'lib/core';
import { PageSizeMenu } from 'lib/pagination';
import { KEY_VALUES, useWindowSize } from 'lib/utilities';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { PageSizeLabel } from '../elements';

const StyledPageSize = styled.div`
  align-items: center;
  display: flex;
  height: ${getRem('36px')};
  margin-right: ${({ theme }) => theme.size.spacing.large.value};
  ${({ $isMobileView }) =>
    $isMobileView &&
    css`
      display: none;
    `};
`;

const StyledPageSizeMenu = styled(PageSizeMenu)`
  margin: 0;
`;

const PageSize = ({
  dataTestId = undefined,
  id,
  onChange = () => {},
  onTabbingIntoTable = undefined,
  rowsPerPageLabel,
  rowsPerPageOptions,
  value,
  ...other
}) => {
  const [selectedOption, setSelectedOption] = useState(value);

  const handleSelect = (option) => {
    setSelectedOption(option);
    onChange(option);
  };

  const windowSize = useWindowSize();
  const isMobileView = isMobileScreen(windowSize.width);

  const handleDropdownKeyDown = (event) => {
    const isTabPressed = event.key === KEY_VALUES.TAB;
    const isShiftPressed = event.shiftKey;

    if (isTabPressed && isShiftPressed) {
      onTabbingIntoTable(event);
    }
  };

  return (
    <StyledPageSize $isMobileView={isMobileView} {...other}>
      <PageSizeLabel id={id} text={rowsPerPageLabel} data-testid={dataTestId ? `${dataTestId}-divider` : undefined} />
      <StyledPageSizeMenu
        dataTestId={dataTestId}
        dropDownOptions={rowsPerPageOptions}
        onKeyDown={handleDropdownKeyDown}
        onSelect={handleSelect}
        pageSize={selectedOption.value.toString()}
      />
    </StyledPageSize>
  );
};
PageSize.propTypes = {
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** Id of the page size selection dropdown */
  id: PropTypes.string.isRequired,
  /** Callback that is called on page size change */
  onChange: PropTypes.func,
  /** Callback function which is called on tabbing into table */
  onTabbingIntoTable: PropTypes.func,
  /** Sets label for rows per page dropdown */
  rowsPerPageLabel: PropTypes.node.isRequired,
  /** List of pagination page size available options */
  rowsPerPageOptions: PropTypes.arrayOf(
    PropTypes.shape({
      /** Label of the dropdown option */
      label: PropTypes.node.isRequired,
      /** Value of the dropdown option */
      value: PropTypes.number.isRequired,
    })
  ).isRequired,
  /** Value of the currently selected page size */
  value: PropTypes.shape({
    /** Label of the dropdown option */
    label: PropTypes.node.isRequired,
    /** Value of the dropdown option */
    value: PropTypes.number.isRequired,
  }).isRequired,
};

export { PageSize };
